import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useBreakpoint } from "@/lib/useBreakpoint";
import { WS_TOKENS } from "@/lib/workspaceTheme";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import WorkspaceSidebar from "@/components/workspace/WorkspaceSidebar";
import WorkspaceBottomNav from "@/components/workspace/WorkspaceBottomNav";
import WorkspaceTopBar from "@/components/workspace/WorkspaceTopBar";

/**
 * AppShell v2 — the internal workspace chrome.
 *
 * Responsibilities:
 *   • Activates workspace theme (deep navy palette) via I18nContext.
 *   • Renders Sidebar (desktop ≥ 1024px) OR Bottom Nav (< 1024px).
 *   • Renders a unified 56px Top Bar with title, notifications, avatar.
 *   • Applies iOS safe-area padding.
 *
 * Non-goals: no branding, no logic, no permissions changes. All navigation
 * targets are existing routes verified in App.js.
 */
export default function AppShell({ children }) {
  const { theme, dir, setWorkspaceMode } = useI18n();
  const { isDesktop } = useBreakpoint();
  const { pathname } = useLocation();

  // Activate the workspace palette while the shell is mounted; restore when
  // it unmounts (e.g. logout, ATS reroute).
  useEffect(() => {
    setWorkspaceMode(true);
    return () => setWorkspaceMode(false);
  }, [setWorkspaceMode]);

  const showBackButton = pathname !== "/" && pathname !== "/applications";

  const css = {
    root: {
      minHeight: "100vh",
      background: theme.bg,
      color: theme.text,
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
      display: "flex",
      flexDirection: "row",
      direction: dir,
    },
    main: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      // Reserve gutter for the sidebar on desktop
      marginInlineStart: isDesktop ? WS_TOKENS.sidebarWidth : 0,
      paddingBottom: isDesktop ? 0 : WS_TOKENS.bottomNavHeight + 12,
    },
    content: {
      flex: 1,
      minWidth: 0,
    },
  };

  return (
    <div style={css.root} data-testid="app-shell">
      {isDesktop && <WorkspaceSidebar />}
      <div style={css.main}>
        <WorkspaceTopBar showBack={showBackButton && !isDesktop} />
        <main style={css.content}>{children}</main>
      </div>
      {!isDesktop && <WorkspaceBottomNav />}
      <PushPermissionBanner />
    </div>
  );
}
